import { Flex, PollIcon, ShareAltIcon, Text, Tooltip } from "@fluentui/react-northstar";
import { SurveyCloseCommand, SurveyDeleteCommand, getSurveyTypeDisplay } from "@teams/cards";
import { canCreateWebShareForPolly } from "@teams/client-shared";
import { TeamsUser } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import moment from "moment";
import React, { useState } from "react";

import { useUserInfo } from "../../../../_shared/actions/useUserInfo";
import { AutolinkListDialog } from "../../../../_shared/components/autolink/AutolinkListDialog";
import {
  OverflowMenu,
  OverflowMenuItem,
} from "../../../../_shared/components/Menus/OverflowMenu/OverflowMenu";
import { PollyDialog } from "../../../../_shared/components/PollyDialog";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { RequestCustomAnalyticsDialog } from "../../../../_shared/components/RequestCustomAnalyticsDialog";
import { ResultsExportCompletionDialog } from "../../../../_shared/components/ResultsExportCompletionDialog";
import { ShareSurveyDialog } from "../../../../_shared/components/ShareSurveyDialog";
import { UsersAndPermissions } from "../../../../_shared/components/UsersAndPermissions";
import { FetchError, isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";
import { isMobileClient } from "../../../../_shared/utils/clientHelper";
import { clientEventTracker } from "../../../../_shared/utils/clientTelemetry";
import { downloadSurveyCsv } from "../../../../_shared/utils/pollyResultsExport";
import { startTaskGenericConfirmation } from "../../../../_shared/utils/startTaskGenericConfirmation";
import {
  startMeetingSurveyTakingTask,
  startPollyDuplicateTask,
  startPollyEditTask,
  startPollyReopenTask,
} from "../../../../_shared/utils/startTaskWrapper";
import { canReadSurveyResults } from "../../../../_shared/utils/userHelpers";
import { TemplateSaveDialog } from "../../SurveyAuthoring/Templates/TemplateSaveDialog";

type Props = {
  survey: SurveyDTO;
  onChanged: () => void;
  hideOwners?: boolean;
  hideTemplateSave?: boolean;
  hideClose?: boolean;
  noOverflow?: boolean;
  noExposedItems?: boolean;
  showEditResponses?: boolean;
};

export const SurveyMenu: React.FC<Props> = ({
  survey,
  onChanged,
  hideOwners,
  hideTemplateSave,
  hideClose,
  noOverflow,
  noExposedItems,
  showEditResponses,
}) => {
  const { teamsContext } = useTeamsContext();

  const { user } = useUserInfo();
  const [isUsersOpen, setIsUsersOpen] = useState<boolean>(false);
  const [isAutolinkOpen, setIsAutolinkOpen] = useState<boolean>(false);

  const [owners, setOwners] = useState(survey.owners);
  const [changedOwners, setChangedOwners] = useState<boolean>(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState<boolean>(false);
  const [showShare, setShowShare] = useState<boolean>(false);
  const [showCustomAnalyticsDialog, setShowCustomAnalyticsDialog] = useState<boolean>(false);
  const [csvExportSuccess, setCsvExportSuccess] = useState<boolean>();
  const [csvExportResult, setCsvExportResult] = useState<string>();

  const menuItems: OverflowMenuItem[] = [];

  if (isMobileClient(teamsContext)) {
    return null;
  }

  if (showEditResponses) {
    menuItems.push({
      key: "edit-responses",
      content: "Edit Responses",
      showInOverflow: noOverflow ? false : true,
      icon: (
        <Tooltip content="Edit Responses">
          <span>
            <PollyIcon icon="edit-icon" inheritColor />
          </span>
        </Tooltip>
      ),
      onClick: async () => {
        await startMeetingSurveyTakingTask(survey, true);
      },
    });
  }

  if (user.featureFlags.surveyExports && survey.state !== "pending" && survey.role === "own") {
    menuItems.push({
      key: "export",
      content: noExposedItems ? "Export results" : undefined,
      showInOverflow: noExposedItems ? true : false,
      icon: (
        <Tooltip content="Export Results">
          <span>
            <PollyIcon icon="export-icon" inheritColor />
          </span>
        </Tooltip>
      ),
      disabled: !canReadSurveyResults(survey),
      onClick: async () => {
        const result = await downloadSurveyCsv(survey, teamsContext);

        if (isError(result)) {
          console.error(result);
          setCsvExportResult((result as FetchError).body?.message as string);
          setCsvExportSuccess(false);
        } else {
          setCsvExportResult(result);
          setCsvExportSuccess(true);
        }
      },
    });
  }

  if (survey.role === "own") {
    if (user.featureFlags.allowPollyEdit) {
      if (survey.state !== "closed" && survey.type !== "trivia") {
        menuItems.push({
          key: "edit",
          showInOverflow: noOverflow ? false : true,
          icon: <PollyIcon icon="edit-icon" inheritColor />,
          content: "Edit",
          onClick: () => {
            startPollyEditTask(survey, () => {
              onChanged();
            });
          },
        });
      }

      menuItems.push({
        key: "duplicate",
        showInOverflow: noOverflow ? false : true,
        content: "Duplicate",
        icon: <PollyIcon icon="duplicate-polly-icon" inheritColor />,
        onClick: () => {
          startPollyDuplicateTask(survey, () => {
            onChanged();
          });
        },
      });
    }

    if (!hideTemplateSave) {
      menuItems.push({
        key: "save",
        showInOverflow: noOverflow ? false : true,
        icon: <PollyIcon icon="my-templates-icon" inheritColor />,
        content: "Save as Template",
        onClick: () => setSaveTemplateDialogOpen(true),
      });
    }

    if (!hideClose && survey.state === "open") {
      menuItems.push({
        key: "close",
        content: "Close",
        icon: (
          <Tooltip content="Close">
            <span>
              <PollyIcon icon="close-poll-icon" inheritColor />
            </span>
          </Tooltip>
        ),
        onClick: () => {
          const label = getSurveyTypeDisplay(survey.type);
          startTaskGenericConfirmation(
            `Close ${label} now?`,
            "Audience will no longer be able to respond to it unless you reopen it.",
            "Close",
            survey.applicationType,
            onChanged,
            `Close ${label}`,
            "Cancel",
            SurveyCloseCommand(survey.id as string),
            "ALERT_WARN",
            true
          );
        },
      });
    }

    if (
      survey.role === "own" &&
      survey.state === "closed" &&
      survey.type !== "trivia" &&
      user.featureFlags.allowPollyEdit
    ) {
      menuItems.push({
        key: "reopen",
        showInOverflow: noOverflow ? false : true,
        icon: <PollyIcon icon="reopen-icon" inheritColor />,
        content: "Reopen",
        onClick: () => {
          startPollyReopenTask(survey, () => {
            onChanged();
          });
        },
      });
    }

    if (survey.state !== "closed" && canCreateWebShareForPolly(survey.type)) {
      menuItems.push({
        key: "share",
        content: noExposedItems
          ? `Share ${getSurveyTypeDisplay(survey.type, { capitalize: false })}`
          : undefined,
        icon: (
          <Tooltip content={`Share ${getSurveyTypeDisplay(survey.type)}`}>
            <span>
              <ShareAltIcon />
            </span>
          </Tooltip>
        ),
        onClick: () => setShowShare(true),
      });
    }

    if (survey.type === "agile-standup") {
      menuItems.push({
        key: "autolink",
        showInOverflow: false,
        icon: <PollyIcon icon="integration-icon" inheritColor />,
        content: "Integrations",
        onClick: () => setIsAutolinkOpen(true),
      });
    }

    if (!survey.scheduleId && !hideOwners) {
      menuItems.push({
        key: "owners",
        showInOverflow: noOverflow ? false : true,
        icon: <PollyIcon icon="profile-icon" inheritColor />,
        content: "Owners",
        onClick: () => setIsUsersOpen(true),
      });
    }

    menuItems.push({
      key: "delete",
      showInOverflow: noOverflow ? false : true,
      icon: <PollyIcon icon="trash-icon" inheritColor />,
      content: "Delete",
      onClick: () => {
        const label = getSurveyTypeDisplay(survey.type);
        startTaskGenericConfirmation(
          `Delete ${label}?`,
          "All messages & data related to this Polly will be deleted.",
          "Delete",
          survey.applicationType,
          onChanged,
          `Delete ${label}`,
          "Cancel",
          SurveyDeleteCommand(survey.id as string),
          "ALERT_DANGER",
          true
        );
      },
    });

    if (user.featureFlags.allowCustomAnalyticsRequest) {
      menuItems.push({
        key: "request-analytics",
        showInOverflow: noOverflow ? false : true,
        icon: <PollIcon />,
        content: "Request Custom Analytics",
        onClick: () => {
          setShowCustomAnalyticsDialog(true);
        },
      });
    }
  }

  if (!menuItems.length) {
    return null;
  }

  return (
    <>
      <OverflowMenu
        items={menuItems}
        maxExposedItemsCount={noExposedItems ? 0 : noOverflow ? 100 : 2}
      />

      {isUsersOpen && owners && (
        <PollyDialog
          open
          onCancel={() => {
            setIsUsersOpen(false);
            if (changedOwners) {
              onChanged();
            }
          }}
          header={
            <Flex vAlign="center" gap="gap.small">
              <PollyIcon icon="profile-icon" inheritColor />
              <Text content={`Owner access for ${getSurveyTypeDisplay(survey.type)}`} />
            </Flex>
          }
        >
          <div className="series-menu__users-dialog">
            <UsersAndPermissions
              canOwn={survey.role === "own"}
              owners={owners}
              onAddOwner={async owner => {
                if (survey.role !== "own") {
                  return;
                }

                const newOwner: TeamsUser = {
                  aadObjectId: owner.aadObjectId,
                  name: owner.name,
                  teamsUserId: owner.id,
                  upn: owner.userPrincipalName,
                };

                const result = await surveyApi.addOwner(
                  teamsContext,
                  survey.id as string,
                  newOwner
                );
                clientEventTracker.track("polly shared", user, {
                  pollyId: survey.id,
                  addedUser: newOwner.aadObjectId,
                });
                if (!isError(result)) {
                  setOwners([...owners, newOwner]);
                  setChangedOwners(true);
                }
                return result;
              }}
              onRemoveOwner={async userToRemove => {
                if (survey.role !== "own") {
                  return;
                }

                const result = await surveyApi.removeOwner(
                  teamsContext,
                  survey.id as string,
                  userToRemove.aadObjectId
                );
                clientEventTracker.track("polly owner removed", user, {
                  addedUser: userToRemove.aadObjectId,
                });
                if (!isError(result)) {
                  setOwners(owners.filter(o => o.aadObjectId !== userToRemove.aadObjectId));
                  setChangedOwners(true);
                }
                return result;
              }}
            />
          </div>
        </PollyDialog>
      )}

      {isAutolinkOpen && (
        <AutolinkListDialog
          reference={survey}
          referenceType="survey"
          setIsOpen={setIsAutolinkOpen}
          onChanged={onChanged}
        />
      )}

      {saveTemplateDialogOpen && (
        <TemplateSaveDialog
          open
          header={
            <Flex vAlign="center" gap="gap.small">
              <PollyIcon icon="my-templates-icon" inheritColor />
              <Text content="Save as template" />
            </Flex>
          }
          closeOnOutsideClick
          showViewTemplateButton
          onClose={() => setSaveTemplateDialogOpen(false)}
          onSave={() => {
            let openDuration = survey.openDuration;
            if (!openDuration && survey.closeAt) {
              openDuration = `PT${Math.round(
                moment(survey.closeAt).diff(survey.deliverAt, "minutes")
              )}M`;
            }

            return {
              type: survey.type,
              title: survey.title,
              anonymity: survey.anonymity,
              deliveryPreference: survey.deliveryPreference,
              description: survey.description,
              questions: survey.questions,
              resultsVisibility: survey.resultsVisibility,
              openDuration,
              reminders: [],
              commentsEnabled: survey.commentsEnabled,
              commentsAppeal: survey.commentsAppeal,
            };
          }}
        />
      )}

      {showShare && (
        <ShareSurveyDialog
          open
          survey={survey}
          width={teamsContext.page.frameContext === "sidePanel" ? "95%" : undefined}
          maxWidth={teamsContext.page.frameContext === "sidePanel" ? undefined : "500px"}
          onCancel={() => {
            setShowShare(false);
            onChanged();
          }}
        />
      )}

      {showCustomAnalyticsDialog && (
        <RequestCustomAnalyticsDialog
          onClose={() => {
            setShowCustomAnalyticsDialog(false);
          }}
        />
      )}

      <ResultsExportCompletionDialog
        onCancel={() => {
          setCsvExportResult(undefined);
          setCsvExportSuccess(undefined);
        }}
        exportResult={csvExportResult}
        exportSuccess={csvExportSuccess}
      />
    </>
  );
};
